import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import './ManageBilling.css'
import ChargingHistory from './ChargingHistory'
import { UserBillingApi } from '../apis/UserBillingApi'
import { UserSessionsApi } from '../apis/UserSessionsApi'
import UserBillingGrid from './UserBillingGrid/UserBillingGrid'
import { formatAmounts, formatDateNoHour } from '../utilities/generalUtils'
import { useData } from '../data/DataProvider'
import { useUserAccess } from '../hooks/useUserAccess'
import { Icon } from '../components/gp'
import ReloadSettingsPanel from './ReloadSettingsPanel'

const parseMoney = (str) => {
    const s = String(str ?? '')
    const m = s.match(/^(\D*)([\d,]+)(?:\.(\d+))?(.*)$/)
    if (!m) return { sym: '', whole: s, frac: '', suffix: '' }
    return { sym: m[1] || '', whole: m[2], frac: m[3] || '', suffix: m[4] || '' }
}

function AccessFeePanel({ accessFeeCents, propertyName, defaultCardLast4 }) {
    const dollars = (accessFeeCents / 100).toFixed(2)
    return (
        <div
            style={{
                marginTop: 18,
                background: 'var(--gp-card)',
                border: '1px solid var(--gp-hairline)',
                borderRadius: 'var(--gp-r-md)',
                overflow: 'hidden',
            }}
        >
            <div
                style={{
                    padding: '14px 18px',
                    borderBottom: '1px solid var(--gp-hairline)',
                }}
            >
                <div style={{ font: 'var(--gp-body-med)', color: 'var(--gp-ink)' }}>
                    Access fee
                </div>
                <div style={{ font: 'var(--gp-caption)', color: 'var(--gp-ink-mute)', marginTop: 2 }}>
                    Set by property and charged to Primary card ending {defaultCardLast4 || '—'}, collected monthly.
                </div>
            </div>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    padding: '14px 18px',
                }}
            >
                <div
                    style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        background: 'var(--gp-lime-soft)',
                        color: 'var(--gp-brand)',
                        border: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <Icon name="shield" size={18} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ font: 'var(--gp-body-med)', color: 'var(--gp-ink)' }}>Monthly amount</span>
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '4px 10px',
                                borderRadius: 999,
                                background: 'var(--gp-surface)',
                                color: 'var(--gp-ink-mute)',
                                border: '1px solid var(--gp-hairline)',
                                font: 'var(--gp-micro)',
                                letterSpacing: 0.4,
                            }}
                        >
                            View only
                        </span>
                    </div>
                    {propertyName && (
                        <div style={{ font: 'var(--gp-caption)', color: 'var(--gp-ink-mute)', marginTop: 2 }}>
                            Transaction appears as <strong style={{ fontWeight: 500, color: 'var(--gp-ink)' }}>{propertyName} GoPowerEV</strong>
                        </div>
                    )}
                </div>
                <div
                    style={{
                        font: '500 18px/1 var(--gp-font)',
                        color: 'var(--gp-ink)',
                        letterSpacing: -0.2,
                        textAlign: 'right',
                    }}
                >
                    ${dollars}
                </div>
            </div>
        </div>
    )
}

const ManageBilling = (props) => {
    const [userBilling, setUserBilling] = useState({})
    const [userChargingHistory, setUserChargingHistory] = useState({})
    const [activeTab, setActiveTab] = useState('transaction')
    const { userDashboardSessions, userWallet, userSettings, loadInitialData } =
        useData()
    const { isConsumerGuest } = useUserAccess()
    const hasActiveSession = userDashboardSessions?.activeSessions?.length > 0

    const history = useHistory()

    const getBillingItems = (page) => {
        UserBillingApi.get(page, isConsumerGuest() ? 7 : undefined)
            .then((results) => setUserBilling(results))
            .catch((error) => { })
    }

    const getChargingHistory = (page) => {
        UserSessionsApi.get(page, isConsumerGuest() ? 7 : 25)
            .then((results) => setUserChargingHistory(results))
            .catch((error) => { })
    }

    const goToChargingHistory = () => {
        setActiveTab('charging')
        history.push('/dashboard/manage-billing/charging-history')
    }

    const goToTransactionHistory = () => {
        setActiveTab('transaction')
        history.push('/dashboard/manage-billing/transaction-history')
    }

    useEffect(() => {
        if (props.secondPathId === 'transaction-history') {
            setActiveTab('transaction')
        } else if (props.secondPathId === 'charging-history') {
            setActiveTab('charging')
        }
    }, [props.secondPathId])

    useEffect(() => {
        getBillingItems(1)
    }, [props.creditCardChanged])

    useEffect(() => {
        if (!isConsumerGuest()) {
            getChargingHistory(1)
        }
    }, [])

    const paymentMethods = props.userWalletData?.paymentMethods || []
    const defaultCard =
        paymentMethods.find((c) => c.isDefault) || paymentMethods[0] || null

    const accessFee = (props.myProperty?.billingModel?.fees || []).find(
        (fee) => fee.name?.toLowerCase() === 'ev_license_fee'
    )

    // ─── Breadcrumb ───
    const Breadcrumb = () => (
        <nav
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 22,
                flexWrap: 'wrap',
            }}
        >
            <span
                onClick={() => props.setShowManageBilling(false)}
                style={{
                    font: '400 22px/1.2 var(--gp-font)',
                    color: 'var(--gp-ink-mute)',
                    cursor: 'pointer',
                }}
            >
                Dashboard
            </span>
            <span style={{ color: 'var(--gp-ink-mute)', display: 'flex' }}>
                <Icon name="chevron" size={16} stroke={1.5} />
            </span>
            <span style={{ font: '500 22px/1.2 var(--gp-font)', color: 'var(--gp-ink)' }}>
                Manage Billing
            </span>
        </nav>
    )

    // ─── Balance hero ───
    const BalanceCard = () => {
        const raw = props.isRecurring
            ? formatAmounts(props.topUpAmount, false)
            : formatAmounts(userWallet?.creditBalance)
        const { sym, whole, frac } = parseMoney(raw)
        return (
            <div
                style={{
                    background: 'var(--gp-brand)',
                    color: '#FFFFFF',
                    borderRadius: 'var(--gp-r-lg)',
                    padding: '22px 24px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    minHeight: 168,
                    boxShadow: 'var(--gp-shadow-sm)',
                }}
            >
                <div>
                    <div
                        style={{
                            font: '400 11px/1 var(--gp-font)',
                            letterSpacing: 0.6,
                            textTransform: 'uppercase',
                            color: 'var(--gp-lime-soft)',
                            marginBottom: 10,
                        }}
                    >
                        {props.isRecurring ? 'Subscription pricing' : 'Balance as of today'}
                    </div>
                    <div
                        style={{
                            font: '500 44px/1 var(--gp-font)',
                            letterSpacing: -1,
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: 3,
                            color: '#FFFFFF',
                        }}
                        data-testid="cypress-billing-balance"
                    >
                        {sym && (
                            <span style={{ font: '500 24px/1 var(--gp-font)', color: 'var(--gp-lime)' }}>
                                {sym}
                            </span>
                        )}
                        {whole}
                        {frac && <span style={{ color: 'var(--gp-lime)' }}>.{frac}</span>}
                        {props.isRecurring && (
                            <span style={{ font: '400 16px/1 var(--gp-font)', color: 'var(--gp-lime-soft)' }}>
                                /month
                            </span>
                        )}
                    </div>
                </div>
                {!isConsumerGuest() && (
                    <div
                        style={{
                            marginTop: 16,
                            font: 'var(--gp-caption)',
                            color: 'rgba(255,255,255,0.78)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                        }}
                    >
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--gp-lime)' }} />
                        {props.isRecurring
                            ? `Next billing date will be ${formatDateNoHour(props.nextBillDate)}`
                            : 'Auto-reload when balance drops below $10'}
                    </div>
                )}
            </div>
        )
    }

    const showBalance =
        !isConsumerGuest() || (userWallet?.creditBalance < 0)

    // ─── Payment card ───
    const PaymentCard = ({ cardInfo }) => {
        const isPrimary = cardInfo.isDefault
        const hasError = cardInfo.metadata?.cardOk === false
        const role = hasError ? 'CARD ERROR' : isPrimary ? 'PRIMARY' : 'SECONDARY'
        const exp = `${String(cardInfo.card?.expMonth).padStart(2, '0')}/${String(cardInfo.card?.expYear).slice(-2)}`
        const brand = (cardInfo.card?.brand || 'CARD').toUpperCase()
        const dark = isPrimary || hasError
        return (
            <div
                data-testid="cypress-billing-secondary-card"
                title={hasActiveSession ? 'Cannot edit card while charging session is active' : undefined}
                onClick={() => !hasActiveSession && props.openEditModal(cardInfo)}
                style={{
                    background: hasError
                        ? 'linear-gradient(135deg, #8F1E1E 0%, #B42222 100%)'
                        : isPrimary
                            ? 'var(--gp-ink)'
                            : 'var(--gp-surface)',
                    color: dark ? '#FFFFFF' : 'var(--gp-ink)',
                    border: dark ? 'none' : '1px solid var(--gp-hairline)',
                    borderRadius: 'var(--gp-r-lg)',
                    padding: '20px 22px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    minHeight: 168,
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: 'var(--gp-shadow-sm)',
                    cursor: hasActiveSession ? 'not-allowed' : 'pointer',
                    opacity: hasActiveSession ? 0.7 : 1,
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        right: -50,
                        top: -50,
                        width: 160,
                        height: 160,
                        borderRadius: '50%',
                        background: isPrimary
                            ? 'radial-gradient(circle, rgba(182,242,115,0.18), transparent 65%)'
                            : 'radial-gradient(circle, rgba(71,132,105,0.10), transparent 65%)',
                        pointerEvents: 'none',
                    }}
                />
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        position: 'relative',
                    }}
                >
                    <div
                        style={{
                            font: '500 11px/1 var(--gp-font)',
                            letterSpacing: 0.8,
                            color: hasError ? '#FFFFFF' : isPrimary ? 'var(--gp-lime)' : 'var(--gp-ink-mute)',
                        }}
                    >
                        {role}
                    </div>
                    <div style={{ color: dark ? '#FFFFFF' : 'var(--gp-ink-mute)', opacity: dark ? 0.9 : 0.7 }}>
                        <svg width="24" height="18" viewBox="0 0 24 18" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="1.5" y="1.5" width="21" height="15" rx="2.5" />
                            <path d="M1.5 6.5h21" />
                        </svg>
                    </div>
                </div>
                <div
                    style={{
                        font: '500 18px/1 var(--gp-mono)',
                        letterSpacing: 1,
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 14,
                    }}
                >
                    <span style={{ opacity: 0.55 }}>••••</span>
                    <span style={{ opacity: 0.55 }}>••••</span>
                    <span style={{ opacity: 0.55 }}>••••</span>
                    <span>{cardInfo.card?.last4}</span>
                </div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-end',
                        position: 'relative',
                    }}
                >
                    <div>
                        <div
                            style={{
                                font: '400 9px/1 var(--gp-font)',
                                letterSpacing: 0.6,
                                textTransform: 'uppercase',
                                color: dark ? 'rgba(255,255,255,0.55)' : 'var(--gp-ink-mute)',
                                marginBottom: 4,
                            }}
                        >
                            Expires
                        </div>
                        <div style={{ font: '500 13px/1 var(--gp-mono)', letterSpacing: 0.4 }}>
                            {exp}
                        </div>
                    </div>
                    <div
                        style={{
                            font: '600 13px/1 var(--gp-font)',
                            letterSpacing: 1.2,
                            color: hasError ? '#FFFFFF' : isPrimary ? 'var(--gp-lime)' : 'var(--gp-accent)',
                        }}
                    >
                        {brand}
                    </div>
                </div>
            </div>
        )
    }

    const AddCardSlot = () => (
        <button
            type="button"
            data-testid="cypress-billing-new-card"
            onClick={() => props.openModal()}
            style={{
                background: 'transparent',
                border: '1.5px dashed var(--gp-hairline)',
                borderRadius: 'var(--gp-r-lg)',
                minHeight: 168,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                color: 'var(--gp-ink-mute)',
                font: '500 13px/1.2 var(--gp-font)',
            }}
        >
            <div
                style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    border: '1.5px solid currentColor',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
                    <path d="M7 1.5v11M1.5 7h11" />
                </svg>
            </div>
            Add a card
        </button>
    )

    const Tab = ({ active, children, onClick, testid }) => (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            style={{
                height: 38,
                padding: '0 18px',
                borderRadius: 999,
                border: 'none',
                background: active ? 'var(--gp-brand)' : 'transparent',
                color: active ? '#FFFFFF' : 'var(--gp-ink-mute)',
                font: '500 13px/1 var(--gp-font)',
                cursor: 'pointer',
                transition: 'background 0.15s ease, color 0.15s ease',
            }}
        >
            {children}
        </button>
    )

    const exportCsv = () => {
        const items = userBilling?.items || []
        if (!items.length) return
        const header = ['Date', 'Transaction', 'Card Last 4', 'Credits', 'Debits', 'Refunded', 'Balance']
        const rows = items.map((r) => [
            r.createdAt,
            r.chargeType,
            r.paymentLast4 ?? '',
            r.chargeType === 'credit' ? formatAmounts(r.amount) : '',
            r.chargeType === 'debit' ? formatAmounts(r.amount) : '',
            formatAmounts(r.refunded),
            formatAmounts(r.balance),
        ])
        const csv = [header, ...rows]
            .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
            .join('\n')
        const blob = new Blob([csv], { type: 'text/csv' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'transactions.csv'
        a.click()
        URL.revokeObjectURL(url)
    }

    // Pagination lives outside the table card.
    const PageBtn = ({ children, onClick, isActive, disabled }) => (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                border: isActive ? '1px solid var(--gp-brand)' : '1px solid transparent',
                background: isActive ? 'var(--gp-brand)' : 'transparent',
                color: isActive ? '#FFFFFF' : 'var(--gp-ink)',
                font: '500 13px/1 var(--gp-font)',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.35 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {children}
        </button>
    )

    const GpPagination = ({ data, onChange }) => {
        const pages = data?.pages || 0
        if (pages <= 1) return null
        const cur = data?.page || 1
        let start = Math.max(1, cur - 2)
        const end = Math.min(pages, start + 4)
        start = Math.max(1, end - 4)
        const nums = []
        for (let p = start; p <= end; p++) nums.push(p)
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 4,
                    marginTop: 16,
                }}
            >
                <PageBtn onClick={() => onChange(cur - 1)} disabled={cur === 1}>
                    <Icon name="chevron" size={14} style={{ transform: 'rotate(180deg)' }} />
                </PageBtn>
                {nums.map((p) => (
                    <PageBtn key={p} onClick={() => onChange(p)} isActive={p === cur}>
                        {p}
                    </PageBtn>
                ))}
                <PageBtn onClick={() => onChange(cur + 1)} disabled={cur >= pages}>
                    <Icon name="chevron" size={14} />
                </PageBtn>
            </div>
        )
    }

    const activeData = activeTab === 'charging' ? userChargingHistory : userBilling
    const activeOnPage =
        activeTab === 'charging' ? getChargingHistory : getBillingItems

    return (
        <div className="manage-billing-page-container">
            <Breadcrumb />

            <div className="gp-bill-cards">
                {showBalance && <BalanceCard />}
                {paymentMethods.map((cardInfo, index) => (
                    <PaymentCard key={index} cardInfo={cardInfo} />
                ))}
                {paymentMethods.length < 2 && <AddCardSlot />}
            </div>

            {!props.isRecurring && !isConsumerGuest() && defaultCard && (
                <ReloadSettingsPanel
                    currentDollars={
                        props.topUpAmount
                            ? Math.round(props.topUpAmount / 100)
                            : 0
                    }
                    milesPerWeek={userSettings?.milesPerWeek}
                    defaultCardLast4={defaultCard.card?.last4}
                    onSaved={() => loadInitialData()}
                />
            )}

            {!isConsumerGuest() && (props.isRecurring || accessFee) && (
                <AccessFeePanel
                    accessFeeCents={props.isRecurring ? props.topUpAmount : accessFee?.price}
                    propertyName={props.myProperty?.name}
                    defaultCardLast4={defaultCard?.card?.last4}
                />
            )}

            <div
                style={{
                    margin: '28px 0 22px',
                    height: 1,
                    background:
                        'repeating-linear-gradient(to right, var(--gp-hairline) 0, var(--gp-hairline) 4px, transparent 4px, transparent 8px)',
                }}
            />

            <div className="gp-bill-tabs">
                <Tab
                    active={activeTab === 'transaction'}
                    onClick={goToTransactionHistory}
                    testid="cypress-billing-transHistory-button"
                >
                    Transaction History
                </Tab>
                {!isConsumerGuest() && (
                    <Tab
                        active={activeTab === 'charging'}
                        onClick={goToChargingHistory}
                        testid="cypress-billing-chargeHistory-button"
                    >
                        Charging History
                    </Tab>
                )}
            </div>

            <div className="gp-bill-tablecard">
                {activeTab === 'transaction' && (
                    <div className="gp-tx-header">
                        <div>
                            <div style={{ font: 'var(--gp-body-med)' }}>Recent transactions</div>
                            <div style={{ font: 'var(--gp-caption)', color: 'var(--gp-ink-mute)', marginTop: 2 }}>
                                {userBilling?.pages
                                    ? `Page ${userBilling.page || 1} of ${userBilling.pages}`
                                    : 'Recent activity'}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={exportCsv}
                            style={{
                                height: 34,
                                padding: '0 14px',
                                border: '1px solid var(--gp-hairline)',
                                background: 'var(--gp-card)',
                                borderRadius: 999,
                                font: '500 12px/1 var(--gp-font)',
                                color: 'var(--gp-ink)',
                                cursor: 'pointer',
                            }}
                        >
                            Export CSV
                        </button>
                    </div>
                )}

                {activeTab === 'charging' && !isConsumerGuest() ? (
                    <ChargingHistory
                        getChargingHistory={getChargingHistory}
                        userChargingHistory={userChargingHistory}
                        isRecurring={props.isRecurring}
                        powerType={props.powerType}
                    />
                ) : (
                    <UserBillingGrid
                        getBillingItems={getBillingItems}
                        userBillingItems={userBilling}
                        isRecurring={props.isRecurring}
                        isGuest={isConsumerGuest()}
                    />
                )}
            </div>

            <GpPagination data={activeData} onChange={activeOnPage} />
        </div>
    )
}

export default ManageBilling
